// @ts-strict-ignore
import React, { useMemo } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { BasicStarFilledIcon } from './ui/Icons';
import { BasicHalfStarFilledIcon } from './ui/Icons/BasicHalfStarFilledIcon';
import BodyText from './ui/BodyText';

// These are used for test ids
const STAR_FILLED = 'pro-ratings-star';
const STAR_HALF = 'pro-ratings-star-half';
const STAR_EMPTY = 'pro-ratings-star-empty';

interface StarIconProps {
  rating: number;
  index: number;
  size: number;
  hollow?: boolean;
}

const StarIcon: React.FCWithChildren<StarIconProps> = ({
  rating,
  index,
  size,
  hollow,
}) => {
  const numFullStars = Math.floor(rating);
  const remainder = rating - numFullStars;
  const commonProps = {
    width: size,
    height: size,
  };
  const isFullStar = index <= numFullStars
    || (index === numFullStars + 1 && remainder > 0.5);
  const isHalfStar = index === numFullStars + 1 && remainder > 0;

  if (isFullStar) {
    return (
      <Box testID={STAR_FILLED} dataSet={{ rating: index }}>
        <BasicStarFilledIcon
          {...commonProps}
          color={BrandColor.Yellow}
        />
      </Box>
    );
  }
  if (isHalfStar) {
    return (
      <Box testID={STAR_HALF} dataSet={{ rating: index }}>
        <BasicHalfStarFilledIcon
          {...commonProps}
          color={BrandColor.Yellow}
          backgroundColor={hollow ? BrandColor.WhiteWash : BrandColor.Silver}
          outlineColor={hollow ? BrandColor.Silver : undefined}
        />
      </Box>
    );
  }

  return (
    <Box testID={STAR_EMPTY} dataSet={{ rating: index }}>
      <BasicStarFilledIcon
        {...commonProps}
        color={hollow ? BrandColor.WhiteWash : BrandColor.Silver}
        outlineColor={hollow ? BrandColor.Silver : undefined}
      />
    </Box>
  );
};

interface StarProps {
  onRatingChange?: (index: number) => void;
  rating: number;
  index: number;
  size: number;
}

const Star: React.FCWithChildren<StarProps> = ({
  onRatingChange,
  index,
  rating,
  size,
}) => (
  <TouchableOpacity
    onPress={() => onRatingChange?.(index)}
    accessibilityRole="button"
    disabled={!onRatingChange}
  >
    <Box>
      <StarIcon
        rating={rating}
        index={index}
        size={size}
        hollow={!!onRatingChange}
      />
    </Box>
  </TouchableOpacity>
);

export type RatingStarsProps = {
  ratingStars?: number;
  /** bigStars: (Boolean, Optional) specifies the size of the stars */
  bigStars?: boolean;
  /** onRatingChange: (Function, Optional) a callback that is
   * executed when the user clicks on a star. Can be omitted safely.
   */
  onRatingChange?: (newRating: number) => void;
  /**
   * size = the font size of the star glyph, defaults to 11.
   * if this prop is passed along with `bigStars` bigStars will
   * overwrite this prop and set the size to 45
   */
  size?: number;
  /**
   * The number of star ratings. Include this prop if you want
   * to display the ratings summary information
   */
  ratingsCount?: number;
  /**
   * The number of written reviews. Include this prop if you want
   * to display the review summary information
   */
  reviewCount?: number;
  smallText?: boolean;
  /** True to show only a single star that is always filled */
  condensed?: boolean;
};

const DEFAULT_STAR_SIZE = 11;
const BIG_STAR_SIZE = 45;

/**
 * RatingStars - a 1-5 star rating based on the passed parameter.
 *
 * Features:
 * - Can accept decimal numbers like 3.5 and will show partial stars
 * - Can accept an optional call back which allows this component to
 *   be used as an input for stars.
 */
const RatingStars: React.FCWithChildren<RatingStarsProps> = ({
  onRatingChange,
  bigStars,
  size: sizeProp,
  smallText = false,
  ratingStars: rating = null,
  reviewCount = 0,
  ratingsCount = 0,
  condensed = false,
}) => {
  const isEditable = !!onRatingChange && typeof onRatingChange === 'function';
  const size = useMemo(() => {
    if (bigStars) {
      return BIG_STAR_SIZE;
    }
    return sizeProp || DEFAULT_STAR_SIZE;
  }, [
    bigStars,
    sizeProp,
  ]);

  const handleChangeRating = (
    newRating: number,
  ) => {
    if (newRating) {
      onRatingChange?.(newRating);
    }
  };
  const onChange = isEditable ? handleChangeRating : null;
  const textAppearance = smallText ? 'smaller' : 'small';

  return (
    <Inline
      testID="rating-stars"
      space={2}
      alignItems="center"
    >
      <Star
        rating={rating}
        onRatingChange={onChange}
        index={1}
        size={size}
      />
      {condensed ? null : (
        <React.Fragment>
          <Star
            rating={rating}
            onRatingChange={onChange}
            index={2}
            size={size}
          />
          <Star
            rating={rating}
            onRatingChange={onChange}
            index={3}
            size={size}
          />
          <Star
            rating={rating}
            onRatingChange={onChange}
            index={4}
            size={size}
          />
          <Star
            rating={rating}
            onRatingChange={onChange}
            index={5}
            size={size}
          />
        </React.Fragment>
      )}
      {!!(ratingsCount || reviewCount || rating) && (
        <Inline space={2}>
          {!ratingsCount && !!reviewCount && (
            <BodyText appearance={textAppearance}>
              ({reviewCount})
            </BodyText>
          )}
          {!!(ratingsCount || condensed) && !!rating && (
            <BodyText appearance={textAppearance} weight="bold">
              {rating.toFixed(1)}
            </BodyText>
          )}
          {ratingsCount >= 5 && (
            <BodyText testID="ss-pro-ratings-count" appearance={textAppearance}>
              ({ratingsCount})
            </BodyText>
          )}
        </Inline>
      )}
    </Inline>
  );
};

export default RatingStars;
